import { useRef, useState } from 'react';
import { Slash, Spline, Trash2, Undo2 } from 'lucide-react';
import { Button } from '@higgins/ui';
import { ACCESS_LINE_EDGE, ACCESS_TYPES, accessLineShape, accessType, type AccessLine, type AccessTypeKey, type PlanPoint } from '@higgins/condition-library';
import { curvePath, extendStroke, lineAt, linePixels, strokeToLine, type DrawTool, type PlacedLine } from '../lib/access-lines';
import type { DeviceRecord } from '../lib/device-db';
import type { SitePlanDoc } from '../lib/site-plan';
import { TOUCH_TARGET_PX, useUnitsPerPx } from '../lib/plan-scale';
import { PlanOverlay } from './plan-overlay';

/** Screen movement (px) under which a press is a tap (choose a line), not a stroke. */
const TAP_SLOP_PX = 8;
/** Screen movement (px) between the points kept while drawing: enough to follow a curve, few enough to keep up. */
const POINT_STEP_PX = 3;
/** The two ways to draw (#613); freehand stays for curved elevations. */
const TOOLS: { key: DrawTool; label: string; Icon: typeof Spline }[] = [
  { key: 'freehand', label: 'Freehand', Icon: Spline },
  { key: 'straight', label: 'Straight line', Icon: Slash },
];
/** The chosen line's glow (white), drawn under it so it stands out from the others. */
const CHOSEN_GLOW = '#ffffff';

interface Stroke {
  pointerId: number;
  points: PlanPoint[];
  from: { x: number; y: number };
  last: { x: number; y: number };
  moved: boolean;
}

const readyHint = (areaName: string, tool: DrawTool): string =>
  tool === 'straight' ? `Put a finger or the Pencil on one end of ${areaName}, drag to the other end and lift: a straight line.` : `Draw along ${areaName} with a finger or the Pencil. Each stroke is one line.`;

interface Props {
  plan: SitePlanDoc;
  image: string;
  /** The job's External areas, in area order: the ones a line can be drawn along. */
  areas: DeviceRecord[];
  lines: PlacedLine[];
  /** Save a drawn line on an area; resolves with its visit area's id. */
  onDraw: (area: DeviceRecord, line: AccessLine) => Promise<string>;
  onRemove: (visitAreaId: string, lineId: string) => void;
}

/**
 * The plan with its access lines, and the tools to draw them: choose an
 * External area and an access type, then draw along the elevation with a
 * finger or the Pencil — freehand, or a straight line from where the finger
 * goes down to where it lifts. Each stroke is one line. Tap a line to choose it (to
 * delete it); Undo takes off the last line drawn here. No photo pins: the
 * access plan shows only access.
 */
export function AccessPlanView({ plan, image, areas, lines, onDraw, onRemove }: Props) {
  const svg = useRef<SVGSVGElement>(null);
  const unitsPerPx = useUnitsPerPx(svg, plan.width);
  const [areaId, setAreaId] = useState<string | null>(areas.length === 1 ? areas[0].id : null);
  const [type, setType] = useState<AccessTypeKey | null>(null);
  const [tool, setTool] = useState<DrawTool>('freehand');
  const [stroke, setStroke] = useState<Stroke | null>(null);
  const [chosen, setChosen] = useState<string | null>(null);
  const [drawn, setDrawn] = useState<{ visitAreaId: string; lineId: string }[]>([]);
  const [hint, setHint] = useState<string | null>(null);
  const shape = accessLineShape(plan.width);
  const area = areas.find((a) => a.id === areaId) ?? null;
  const ready = Boolean(area && type);
  const chosenLine = lines.find((l) => l.line.id === chosen) ?? null;

  /** A pointer's place on the plan, in plan pixels. */
  function at(e: React.PointerEvent): PlanPoint {
    const box = svg.current!.getBoundingClientRect();
    return [Math.min(1, Math.max(0, (e.clientX - box.left) / box.width)) * plan.width, Math.min(1, Math.max(0, (e.clientY - box.top) / box.height)) * plan.height];
  }

  function onDown(e: React.PointerEvent) {
    if (stroke) return; // a second finger (or the palm) while drawing
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
    const screen = { x: e.clientX, y: e.clientY };
    setStroke({ pointerId: e.pointerId, points: [at(e)], from: screen, last: screen, moved: false });
  }

  function onMove(e: React.PointerEvent) {
    if (!stroke || e.pointerId !== stroke.pointerId) return;
    if (Math.hypot(e.clientX - stroke.last.x, e.clientY - stroke.last.y) < POINT_STEP_PX) return;
    const moved = stroke.moved || Math.hypot(e.clientX - stroke.from.x, e.clientY - stroke.from.y) > TAP_SLOP_PX;
    setStroke({ ...stroke, points: extendStroke(stroke.points, at(e), tool), last: { x: e.clientX, y: e.clientY }, moved });
  }

  function onUp(e: React.PointerEvent) {
    if (!stroke || e.pointerId !== stroke.pointerId) return;
    setStroke(null);
    setHint(null);
    if (!stroke.moved) {
      const tapped = lineAt(lines, at(e), plan, Math.max(shape.width, TOUCH_TARGET_PX * unitsPerPx) / 2);
      setChosen(tapped && tapped.line.id !== chosen ? tapped.line.id : null);
      return;
    }
    if (!area || !type) {
      setHint('Choose an area and an access type first, then draw along the area.');
      return;
    }
    const line = strokeToLine(extendStroke(stroke.points, at(e), tool), plan, type, crypto.randomUUID());
    if (!line) return;
    void onDraw(area, line).then((visitAreaId) => setDrawn((d) => [...d, { visitAreaId, lineId: line.id }]));
  }

  function undo() {
    const last = drawn[drawn.length - 1];
    if (!last) return;
    setDrawn(drawn.slice(0, -1));
    if (chosen === last.lineId) setChosen(null);
    onRemove(last.visitAreaId, last.lineId);
  }

  function removeChosen() {
    if (!chosenLine) return;
    setDrawn(drawn.filter((d) => d.lineId !== chosenLine.line.id));
    onRemove(chosenLine.visitAreaId, chosenLine.line.id);
    setChosen(null);
  }

  const strokeColour = type ? accessType(type)?.colour : undefined;

  return (
    <div className="space-y-2">
      <div className="space-y-2 rounded-lg border border-slate-200 bg-white p-2">
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Area">
          <span className="text-xs text-slate-500">Area</span>
          {areas.map((a) => {
            const count = lines.filter((l) => l.areaId === a.id).length;
            return (
              <Button key={a.id} variant={a.id === areaId ? 'default' : 'outline'} className="h-11" aria-pressed={a.id === areaId} onClick={() => setAreaId(a.id)}>
                {String(a.fields.hig_name ?? '')}
                {count ? ` (${count})` : ''}
              </Button>
            );
          })}
          {areas.length === 0 && <span className="text-sm text-slate-500">No External areas on this job yet. Add one on the job, then draw its access here.</span>}
        </div>
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Access type">
          <span className="text-xs text-slate-500">Access</span>
          {ACCESS_TYPES.map((t) => (
            <Button key={t.key} variant={t.key === type ? 'default' : 'outline'} className="h-11 gap-2" aria-pressed={t.key === type} onClick={() => setType(t.key)}>
              <span className="inline-block h-3 w-6 rounded-full border" style={{ background: t.colour, borderColor: ACCESS_LINE_EDGE }} aria-hidden />
              {t.label}
            </Button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Draw">
          <span className="text-xs text-slate-500">Draw</span>
          {TOOLS.map(({ key, label, Icon }) => (
            <Button key={key} variant={key === tool ? 'default' : 'outline'} className="h-11 gap-2" aria-pressed={key === tool} onClick={() => setTool(key)}>
              <Icon className="h-4 w-4" aria-hidden /> {label}
            </Button>
          ))}
          <Button variant="ghost" className="ml-auto h-11 gap-1" disabled={!drawn.length} onClick={undo}>
            <Undo2 className="h-4 w-4" aria-hidden /> Undo
          </Button>
        </div>
        <p className="text-xs text-slate-500" role="status">
          {hint ?? (ready ? readyHint(String(area?.fields.hig_name ?? ''), tool) : 'Choose an area and an access type, then draw along the area. Tap a line to delete it.')}
        </p>
      </div>

      <PlanOverlay
        plan={plan}
        image={image}
        svgRef={svg}
        crosshair={ready}
        label="Access lines on the site plan"
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={() => setStroke(null)}
      >
          {lines.map(({ line }) => {
            const colour = accessType(line.type)?.colour;
            const d = curvePath(linePixels(line, plan));
            return (
              <g key={line.id}>
                {line.id === chosen && <path d={d} fill="none" stroke={CHOSEN_GLOW} strokeWidth={shape.width * 2.2} strokeLinecap="round" strokeLinejoin="round" opacity={0.85} />}
                <path d={d} fill="none" stroke={ACCESS_LINE_EDGE} strokeWidth={shape.width + 2 * shape.edge} strokeLinecap="round" strokeLinejoin="round" />
                <path d={d} fill="none" stroke={colour} strokeWidth={shape.width} strokeLinecap="round" strokeLinejoin="round" />
              </g>
            );
          })}
          {stroke?.moved && ready && (
            <polyline points={stroke.points.map((p) => p.join(',')).join(' ')} fill="none" stroke={strokeColour} strokeWidth={shape.width} strokeLinecap="round" strokeLinejoin="round" opacity={0.7} />
          )}
      </PlanOverlay>

      {chosenLine && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
          <span className="inline-block h-3 w-6 shrink-0 rounded-full border" style={{ background: accessType(chosenLine.line.type)?.colour, borderColor: ACCESS_LINE_EDGE }} aria-hidden />
          <span className="min-w-0 flex-1">
            {accessType(chosenLine.line.type)?.label} · {chosenLine.areaName}
          </span>
          <Button variant="outline" className="h-11 gap-1" onClick={removeChosen}>
            <Trash2 className="h-4 w-4" aria-hidden /> Delete line
          </Button>
          <Button variant="ghost" className="h-11" onClick={() => setChosen(null)}>
            Done
          </Button>
        </div>
      )}
    </div>
  );
}
